import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Card,
  Empty,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  thStyle,
} from '@/components/ui';
import { useAuth } from '@/store/auth';

interface Funnel {
  overall: {
    booked: number;
    arrived: number;
    consulted: number;
    converted: number;
    completed: number;
    cancelled: number;
    arrivalRatePct: number;
    consultRatePct: number;
    conversionRatePct: number;
    completionRatePct: number;
    cancelRatePct: number;
  };
  byConsultant: {
    staffId: string;
    name: string;
    role: string;
    booked: number;
    consultations: number;
    converted: number;
    conversionRatePct: number;
  }[];
}

const roleLabel = (r: string) =>
  ({ TENANT_ADMIN: '院長 / 老闆', STORE_MANAGER: '店長', DOCTOR: '醫師', CONSULTANT: '諮詢師', ESTHETICIAN: '美容師', FRONT_DESK: '櫃檯' }[r] ?? r);

const rateTone = (p: number): Parameters<typeof Tag>[0]['tone'] =>
  p >= 60 ? 'success' : p >= 35 ? 'warn' : 'danger';

export function FunnelPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const q = useQuery({
    queryKey: ['consultation-funnel'],
    queryFn: () => api.get<Funnel>('/finance/consultation-funnel'),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="諮詢轉換漏斗" subtitle="成交率 · 諮詢師績效" />
        <Card><Empty>🔒 諮詢績效屬業績機密,僅租戶管理者可檢視。</Empty></Card>
      </div>
    );
  }

  const o = q.data?.overall;
  const stages = o
    ? [
        { label: '預約', value: o.booked, rate: null as number | null },
        { label: '到診', value: o.arrived, rate: o.arrivalRatePct },
        { label: '諮詢', value: o.consulted, rate: o.consultRatePct },
        { label: '成交', value: o.converted, rate: o.conversionRatePct },
        { label: '完成', value: o.completed, rate: o.completionRatePct },
      ]
    : [];
  const max = o?.booked || 1;

  return (
    <div>
      <PageHeader title="諮詢轉換漏斗" subtitle="預約 → 到診 → 諮詢 → 成交 → 完成,看每位諮詢師把客人帶到哪一關" />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="整體成交率" value={`${o?.conversionRatePct ?? 0}%`} tone="primary" hint="成交 / 諮詢" />
        <Stat label="到診率" value={`${o?.arrivalRatePct ?? 0}%`} hint="到診 / 預約" />
        <Stat label="完成率" value={`${o?.completionRatePct ?? 0}%`} hint="完成 / 成交" />
        <Stat label="取消率" value={`${o?.cancelRatePct ?? 0}%`} tone={o && o.cancelRatePct >= 20 ? 'danger' : 'default'} hint={`${o?.cancelled ?? 0} 筆`} />
      </div>

      {/* 漏斗 */}
      <Card title="轉換漏斗" icon="🫙">
        {!o ? (
          <Empty>尚無預約資料。</Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {stages.map((s) => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, fontSize: 13, color: 'var(--ink-2)', fontWeight: 600 }}>{s.label}</div>
                <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 6, overflow: 'hidden', height: 30 }}>
                  <div
                    style={{
                      width: `${Math.max((s.value / max) * 100, 3)}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, var(--primary), var(--primary-deep))',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 10,
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      minWidth: 40,
                    }}
                    className="num"
                  >
                    {s.value}
                  </div>
                </div>
                <div style={{ width: 110, fontSize: 12, color: 'var(--ink-3)' }}>
                  {s.rate !== null ? `↳ 上一關 ${s.rate}%` : '基準'}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 諮詢師排行 */}
      <Card title="諮詢師成交率排行" icon="🏆">
        {!q.data?.byConsultant.length ? (
          <Empty>尚無可歸屬的諮詢資料(需有 soldBy / 主責歸屬)。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>排名</th>
                <th style={thStyle}>諮詢師</th>
                <th style={thStyle}>角色</th>
                <th style={thStyle}>諮詢數</th>
                <th style={thStyle}>成交數</th>
                <th style={thStyle}>成交率</th>
              </tr>
            </thead>
            <tbody>
              {q.data.byConsultant.map((r, i) => (
                <tr key={r.staffId}>
                  <td style={cellStyle} className="num">{i + 1}</td>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{r.name}</td>
                  <td style={cellStyle}><Tag>{roleLabel(r.role)}</Tag></td>
                  <td style={cellStyle} className="num">{r.consultations}</td>
                  <td style={cellStyle} className="num">{r.converted}</td>
                  <td style={cellStyle} className="num">
                    <Tag tone={rateTone(r.conversionRatePct)}>{r.conversionRatePct}%</Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 8 }}>
        ※ 成交 = 諮詢後實際進入療程(治療中 / 已完成)。歸屬以開單者為準,缺則回退顧客主責諮詢師。
      </div>
    </div>
  );
}
